import React, { useEffect, useRef, useState, useMemo } from 'react';
import { GradientText } from '../../atoms/GradientText';
import { MetricItem } from '../../molecules/MetricItem';
import { usePublicMetrics } from '../../../hooks/useMetrics';
import { useLanguage } from '@/contexts/LanguageContext';
import type { PublicMetric } from '@lnr-v2/types';
import styles from './MetricsSection.module.css';
import { CTAButton } from '@/components/atoms';
import { useNavigate } from 'react-router-dom';

// Gradient markers for different languages
const GRADIENT_MARKERS = {
  en: 'impact in numbers',
  fr: 'qui nous rendent fiers',
} as const;

type SupportedLanguage = keyof typeof GRADIENT_MARKERS;

interface MetricsSectionProps {
  className?: string;
}

// Fallback metric keys - captions come from translations
const FALLBACK_METRIC_KEYS = [
  { value: '1K', captionKey: 'home.metrics.fallback.projects' },
  { value: '80', captionKey: 'home.metrics.fallback.awards' },
  { value: '13', captionKey: 'home.metrics.fallback.experience' },
  { value: '22', captionKey: 'home.metrics.fallback.talents' },
  { value: '53', captionKey: 'home.metrics.fallback.freelancers' },
];

export const MetricsSection: React.FC<MetricsSectionProps> = ({
  className,
}) => {
  const { t, language } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const prevMetricsRef = useRef<PublicMetric[]>([]);
  const navigate = useNavigate();

  // Fetch dynamic metrics from API with enhanced error handling
  const {
    metrics: apiMetrics,
    loading,
    error,
  } = usePublicMetrics({
    autoFetch: true,
    lang: language as 'en' | 'fr',
    onError: (err) => {
      console.warn('Metrics fetch error, using fallback:', err.message);
      console.warn('Error details:', err);
    },
  });

  // Build fallback metrics with translations
  const fallbackMetrics = useMemo(
    () =>
      FALLBACK_METRIC_KEYS.map((item) => ({
        value: item.value,
        caption: t(item.captionKey),
      })),
    [t]
  );

  // Format metrics for display with proper fallback
  const metrics = useMemo(() => {
    // Use fallback while loading initially or if there's an error
    if ((loading && !apiMetrics?.length) || (error && !apiMetrics?.length)) {
      return fallbackMetrics;
    }

    // If we have API metrics, use them
    if (apiMetrics && apiMetrics.length > 0) {
      return apiMetrics
        .sort((a, b) => a.sortOrder - b.sortOrder) // Sort by sortOrder
        .slice(0, 5) // Limit to 5 metrics
        .map((metric) => {
          // Simple formatting based on the simplified schema
          let displayValue = String(metric.value);

          // Format large numbers
          const numValue = Number(metric.value);
          if (numValue >= 1000000) {
            displayValue = (numValue / 1000000).toFixed(1) + 'M';
          } else if (numValue >= 1000) {
            displayValue = (numValue / 1000).toFixed(1) + 'K';
          } else if (numValue % 1 !== 0) {
            displayValue = numValue.toFixed(1);
          }

          return {
            value: displayValue,
            caption: metric.label,
            description: metric.description,
          };
        });
    }

    // Fall back to default metrics
    return fallbackMetrics;
  }, [apiMetrics, loading, error, fallbackMetrics]);

  // Detect when metrics update and trigger animation
  useEffect(() => {
    if (apiMetrics && apiMetrics.length > 0) {
      // Check if metrics have actually changed
      const metricsChanged =
        JSON.stringify(apiMetrics) !== JSON.stringify(prevMetricsRef.current);

      if (metricsChanged && prevMetricsRef.current.length > 0) {
        // Trigger update animation
        setIsUpdating(true);
        console.log('Metrics updated from API');

        // Reset animation after duration
        const timeout = setTimeout(() => {
          setIsUpdating(false);
        }, 1000);

        prevMetricsRef.current = apiMetrics;
        return () => clearTimeout(timeout);
      } else if (prevMetricsRef.current.length === 0) {
        // First load, no animation
        prevMetricsRef.current = apiMetrics;
      }
    }
    return undefined;
  }, [apiMetrics]);

  const handleContactClick = () => {
    navigate('/contact');
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const isCompact = window.matchMedia('(max-width: 1024px)').matches;
    if (isCompact) {
      // On mobile, show all metrics
      setActiveIndex(0);
      return;
    }

    const handleScroll = () => {
      if (!sectionRef.current) return;

      const section = sectionRef.current;
      const rect = section.getBoundingClientRect();
      const sectionHeight = section.offsetHeight;
      const windowHeight = window.innerHeight;

      // Calculate how far through the section we've scrolled
      // When section top hits viewport top, progress = 0
      // When section bottom hits viewport bottom, progress = 1
      const scrollProgress = Math.max(
        0,
        Math.min(1, (windowHeight - rect.top) / (sectionHeight - windowHeight))
      );

      // Map scroll progress to metric index
      const newIndex = Math.min(
        Math.floor(scrollProgress * metrics.length),
        metrics.length - 1
      );

      setActiveIndex(newIndex);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial calculation

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [metrics.length]);

  return (
    <section
      ref={sectionRef}
      className={`${styles.metricsSection} ${className || ''}`}
      aria-label="Key metrics"
    >
      <div className={styles.inner}>
        <div className={styles.metricsColumn}>
          <div className={styles.metricStage}>
            {metrics.map((metric, index) => (
              <div
                key={metric.caption} // Use caption as key for stable animation
                className={`${styles.metricStageItem} ${
                  activeIndex === index ? styles.metricStageItemActive : ''
                } ${isUpdating ? styles.metricStageItemUpdating : ''}`}
                aria-hidden={activeIndex !== index}
              >
                <MetricItem value={metric.value} caption={metric.caption} />
              </div>
            ))}
          </div>
        </div>
        <div className={styles.contentColumn}>
          <p className={styles.sectionLabel}>{t('home.metrics.label')}</p>
          <h3 className={styles.sectionTitle}>
            {(() => {
              const fullTitle = t('home.metrics.title');
              const lang = language as SupportedLanguage;
              const gradientPart =
                GRADIENT_MARKERS[lang] ?? GRADIENT_MARKERS.en;
              const titleWithoutGradient = fullTitle
                .replace(gradientPart, '')
                .trim();
              return (
                <>
                  {titleWithoutGradient}{' '}
                  <GradientText className={styles.highlight}>
                    {gradientPart}
                  </GradientText>
                </>
              );
            })()}
          </h3>
          <CTAButton onClick={handleContactClick} className={styles.metricsCta}>
            {t('home.metrics.cta')}
          </CTAButton>
        </div>
      </div>
    </section>
  );
};
